import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { safeCopy } from '../utils/clipboard';
import {
    getStockDetailed, getCategories, getStockReport, getWarehouses, createMovement,
    getMovements, deleteMovement,
    createWarehouse, updateWarehouse, deleteWarehouse,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
    Package, Search, ChevronUp, ChevronDown, ChevronRight, Layers, FileSpreadsheet, Edit3, X,
    ArrowDownRight, ArrowUpRight, Trash2, ArrowLeftRight, Printer,
    Plus, Warehouse as WarehouseIcon, Star, Edit2, Copy, QrCode, Monitor,
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import Pagination from '../components/Pagination';
import { useCachedData, invalidateCache } from '../hooks/useDataCache';

export default function StockPage() {
    const { company } = useAuth();
    const [tab, setTab] = useState('levels');
    const terminalUrl = `${window.location.origin}/terminal/stock/${company?.slug || ''}`;

    return (
        <DashboardLayout>
            <div className="space-y-4" data-testid="stock-page">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h1 className="page-title">Stok</h1>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => { safeCopy(terminalUrl, 'Link kopyalandi'); }} className="btn btn-secondary text-sm" data-testid="copy-terminal-link"><Copy size={14} /> Link</button>
                        <button onClick={() => printTerminalQR(terminalUrl, company?.name)} className="btn btn-secondary text-sm" data-testid="print-terminal-qr"><QrCode size={14} /> QR Yazdir</button>
                        <a href={terminalUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary text-sm" data-testid="open-stock-terminal"><Monitor size={14} /> Terminal</a>
                    </div>
                </div>

                <div className="flex gap-1 border-b border-gray-100 pb-px">
                    {[
                        { k: 'levels', l: 'Stok Seviyeleri' },
                        { k: 'movements', l: 'Hareketler' },
                        { k: 'warehouses', l: 'Depolar' },
                    ].map(t => (
                        <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.k ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`} data-testid={`tab-${t.k}`}>
                            {t.l}
                        </button>
                    ))}
                </div>

                {tab === 'levels' && <StockLevelsTab />}
                {tab === 'movements' && <MovementsTab />}
                {tab === 'warehouses' && <WarehousesTab />}
            </div>
        </DashboardLayout>
    );
}

// ======== STOCK LEVELS TAB ========
function StockLevelsTab() {
    const { user } = useAuth();
    const isAdmin = user?.is_admin || user?.type === 'owner';
    const [items, setItems] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchStock = useCallback(() => getStockDetailed(), []);
    const fetchWarehouses = useCallback(() => getWarehouses(), []);
    const { data: cachedStock, invalidate: refreshStock } = useCachedData('stockDetailed', fetchStock);
    const { data: cachedWarehouses } = useCachedData('warehouses', fetchWarehouses);

    useEffect(() => { if (cachedStock) { setItems(cachedStock); setLoading(false); } }, [cachedStock]);
    useEffect(() => { if (cachedWarehouses) setWarehouses(cachedWarehouses); }, [cachedWarehouses]);

    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [sortField, setSortField] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [expanded, setExpanded] = useState(new Set());
    const [reportLoading, setReportLoading] = useState(false);
    const [adjustModal, setAdjustModal] = useState(null);
    const [adjustQty, setAdjustQty] = useState('');
    const [adjustMode, setAdjustMode] = useState('IN');
    const [adjustSaving, setAdjustSaving] = useState(false);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 20;

    const loadData = () => { invalidateCache('stockDetailed'); refreshStock(); };

    const grouped = useMemo(() => {
        const products = items.filter(i => i.type === 'product');
        const variantMap = {};
        items.filter(i => i.type === 'variant').forEach(v => {
            if (!variantMap[v.parent_id]) variantMap[v.parent_id] = [];
            variantMap[v.parent_id].push(v);
        });
        return products.map(p => ({
            ...p,
            display_quantity: variantMap[p.id] ? variantMap[p.id].reduce((s, v) => s + v.quantity, 0) : p.quantity,
            variants: variantMap[p.id] || [],
        }));
    }, [items]);

    const handleSort = (field) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('asc'); }
    };

    const filtered = useMemo(() => {
        setPage(1);
        let list = grouped.filter(p => {
            const q = searchQuery.toLowerCase();
            if (!(p.name.toLowerCase().includes(q) || p.reference?.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q))) return false;
            if (categoryFilter && p.category !== categoryFilter) return false;
            if (filter === 'low') return p.is_low_stock;
            if (filter === 'ok') return !p.is_low_stock && p.display_quantity > 0;
            return true;
        });
        if (sortField) {
            list = [...list].sort((a, b) => {
                const f = sortField === 'quantity' ? 'display_quantity' : sortField;
                let va = a[f] ?? (typeof b[f] === 'number' ? 0 : '');
                let vb = b[f] ?? (typeof a[f] === 'number' ? 0 : '');
                if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
                return sortDir === 'asc' ? String(va).localeCompare(String(vb), 'tr') : String(vb).localeCompare(String(va), 'tr');
            });
        }
        return list;
    }, [grouped, searchQuery, filter, categoryFilter, sortField, sortDir]);

    const toggleExpand = (id) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const openAdjust = (item, isVariant = false, parentName = '') => {
        setAdjustModal({
            product_id: isVariant ? item.parent_id : item.id,
            variant_id: isVariant ? item.id : null,
            name: isVariant ? `${parentName} - ${item.name.trim()}` : item.name,
            current: isVariant ? item.quantity : item.display_quantity ?? item.quantity,
            warehouse_id: item.warehouse_id || (warehouses[0]?.id || ''),
            hasVariants: !isVariant && item.variants?.length > 0,
        });
        setAdjustQty(''); setAdjustMode('IN');
    };

    const handleAdjust = async () => {
        if (!adjustModal) return;
        const qty = parseInt(adjustQty);
        if (isNaN(qty) || qty <= 0) { toast.error('Gecerli bir miktar girin'); return; }
        setAdjustSaving(true);
        try {
            const line = { product_id: adjustModal.product_id, warehouse_id: adjustModal.warehouse_id, quantity: qty };
            if (adjustModal.variant_id) line.variant_id = adjustModal.variant_id;
            await createMovement({ movement_type: adjustMode, notes: `Stok ${adjustMode === 'IN' ? 'giris' : 'cikis'}: ${adjustModal.name}`, lines: [line] });
            toast.success(`${qty} adet ${adjustMode === 'IN' ? 'giris' : 'cikis'}`);
            setAdjustModal(null); loadData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
        finally { setAdjustSaving(false); }
    };

    const handleReport = async () => {
        setReportLoading(true);
        try {
            const r = (await getStockReport()).data;
            const rows = [];
            for (const l of r.lines) {
                rows.push({ 'Referans': l.reference || '', 'Urun': l.name, 'Kategori': l.category || '', 'Stok': l.stock, 'Alis Fiyati': l.purchase_price, 'Alis Toplam': l.purchase_total, 'Satis Fiyati': l.price_a, 'Satis Toplam': l.sale_total });
                if (l.variants?.length) l.variants.forEach(v => rows.push({ 'Referans': v.sku || '', 'Urun': `  - ${v.name}`, 'Stok': v.stock }));
            }
            rows.push({}, { 'Urun': 'TOPLAM', 'Alis Toplam': r.total_purchase, 'Satis Toplam': r.total_sale });
            const ws = XLSX.utils.json_to_sheet(rows); const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Stok Raporu');
            XLSX.writeFile(wb, `stok_raporu_${new Date().toISOString().split('T')[0]}.xlsx`);
            toast.success(`Rapor: Alis ${r.total_purchase.toFixed(2)} / Satis ${r.total_sale.toFixed(2)}`);
        } catch { toast.error('Rapor olusturulamadi'); }
        finally { setReportLoading(false); }
    };

    const total = grouped.length;
    const lowCount = grouped.filter(p => p.is_low_stock).length;
    const uniqueCategories = [...new Set(grouped.map(p => p.category).filter(Boolean))];

    // Admin-only totals
    const totals = useMemo(() => {
        if (!isAdmin) return null;
        let purchaseTotal = 0, saleTotal = 0;
        filtered.forEach(p => {
            purchaseTotal += p.display_quantity * (p.purchase_price || 0);
            saleTotal += p.display_quantity * (p.price_a || 0);
        });
        return { purchase: purchaseTotal, sale: saleTotal };
    }, [filtered, isAdmin]);

    const SortIcon = ({ field }) => { if (sortField !== field) return <ChevronUp size={12} className="opacity-0 group-hover:opacity-30" />; return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />; };
    const ThCell = ({ field, children, align }) => (<th className={`cursor-pointer select-none group ${align === 'right' ? 'text-right' : ''}`} onClick={() => handleSort(field)}><span className="inline-flex items-center gap-1">{children} <SortIcon field={field} /></span></th>);

    const fmt = (v) => v != null ? Math.round(v).toLocaleString('de-DE') : '—';

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-gray-500">{total} urun · {lowCount} dusuk stok</p>
                <button onClick={handleReport} disabled={reportLoading} className="btn btn-secondary text-sm" data-testid="stock-report-btn"><FileSpreadsheet size={15} /> {reportLoading ? 'Hazirlaniyor...' : 'Excel Rapor'}</button>
            </div>

            {/* Admin-only summary cards */}
            {isAdmin && totals && (
                <div className="grid grid-cols-2 gap-3" data-testid="stock-value-summary">
                    <div className="card-apple py-3 px-4">
                        <p className="text-[10px] font-medium text-[#86868B] uppercase tracking-wider">Toplam Alis Degeri</p>
                        <p className="text-lg font-bold text-[#1D1D1F] mt-0.5">{fmt(totals.purchase)} EUR</p>
                    </div>
                    <div className="card-apple py-3 px-4">
                        <p className="text-[10px] font-medium text-[#86868B] uppercase tracking-wider">Toplam Satis Degeri</p>
                        <p className="text-lg font-bold text-[#34C759] mt-0.5">{fmt(totals.sale)} EUR</p>
                    </div>
                </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Ara..." className="form-input pl-10" data-testid="stock-search" />
                </div>
                <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="form-select w-auto text-sm" data-testid="category-filter">
                    <option value="">Tum Kategoriler</option>
                    {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="flex gap-1">
                    {[{k:'all',l:'Tumu'},{k:'low',l:'Dusuk'},{k:'ok',l:'Stokta'}].map(b => (
                        <button key={b.k} onClick={() => setFilter(b.k)} className={`btn text-sm ${filter === b.k ? 'btn-primary' : 'btn-secondary'}`} data-testid={`filter-${b.k}`}>{b.l}</button>
                    ))}
                </div>
            </div>

            <div className="data-card">
                {loading ? <div className="p-6 space-y-3">{[1,2,3,4].map(i => <div key={i} className="skeleton h-10 rounded" />)}</div>
                : filtered.length > 0 ? (
                    <>
                    <div className="table-container">
                        <table className="table mobile-cards" data-testid="stock-table" style={{ tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '32px' }} />
                                <col style={{ width: '120px' }} />
                                <col />
                                <col style={{ width: '100px' }} />
                                <col style={{ width: '90px' }} />
                                <col style={{ width: '80px' }} />
                                {isAdmin && <col style={{ width: '100px' }} />}
                                {isAdmin && <col style={{ width: '100px' }} />}
                                <col style={{ width: '44px' }} />
                            </colgroup>
                            <thead><tr>
                                <th className="w-8"></th>
                                <ThCell field="reference">Referans</ThCell>
                                <ThCell field="name">Urun</ThCell>
                                <ThCell field="category">Kategori</ThCell>
                                <ThCell field="quantity" align="right">Stok</ThCell>
                                <th className="text-right mobile-hide">Palet</th>
                                <th className="text-center">Durum</th>
                                {isAdmin && <th className="text-right">Alis Deg.</th>}
                                {isAdmin && <th className="text-right">Satis Deg.</th>}
                                <th className="w-10"></th>
                            </tr></thead>
                            <tbody>
                                {filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(p => {
                                    const purchaseVal = p.display_quantity * (p.purchase_price || 0);
                                    const saleVal = p.display_quantity * (p.price_a || 0);
                                    return (
                                    <React.Fragment key={p.id}>
                                        <tr data-testid={`stock-row-${p.id}`} className={p.variants.length > 0 ? 'cursor-pointer' : ''} onClick={() => p.variants.length > 0 && toggleExpand(p.id)}>
                                            <td>{p.variants.length > 0 && <ChevronRight size={14} className={`text-gray-400 transition-transform ${expanded.has(p.id) ? 'rotate-90' : ''}`} />}</td>
                                            <td className="font-mono text-xs text-gray-500">{p.reference || '—'}</td>
                                            <td><div className="flex items-center gap-2 truncate">
                                                <span className="font-medium text-gray-900 truncate">{p.name}</span>{p.variants.length > 0 && <span className="text-xs text-gray-400 flex-shrink-0">({p.variants.length})</span>}
                                            </div></td>
                                            <td className="mobile-hide text-gray-500 text-xs truncate">{p.category || '—'}</td>
                                            <td className="text-right"><span className={`text-lg font-semibold tabular-nums ${p.display_quantity <= 0 ? 'text-red-500' : p.is_low_stock ? 'text-amber-500' : 'text-gray-900'}`}>{p.display_quantity.toLocaleString('tr-TR')}</span></td>
                                            <td className="mobile-hide text-right">{p.pallets != null ? <span className="font-medium text-gray-700">{p.pallets}</span> : <span className="text-gray-300">-</span>}</td>
                                            {isAdmin && <td className="text-right text-xs text-gray-500">{fmt(purchaseVal)}</td>}
                                            {isAdmin && <td className="text-right text-xs font-medium text-[#34C759]">{fmt(saleVal)}</td>}
                                            <td>{p.variants.length === 0 && <button onClick={e => { e.stopPropagation(); openAdjust(p); }} className="btn-icon hover:text-blue-600" data-testid={`adjust-stock-${p.id}`}><Edit3 size={14} /></button>}</td>
                                        </tr>
                                        {expanded.has(p.id) && p.variants.map(v => {
                                            const vPurchaseVal = v.quantity * (v.purchase_price || p.purchase_price || 0);
                                            const vSaleVal = v.quantity * (v.price_a || p.price_a || 0);
                                            return (
                                            <tr key={v.id} className="bg-gray-50/70" data-testid={`variant-row-${v.id}`}>
                                                <td></td>
                                                <td className="text-gray-400 font-mono text-xs">{v.reference || '—'}</td>
                                                <td><div className="flex items-center gap-2 pl-4 md:pl-6"><Layers size={12} className="text-blue-400" /><span className="text-sm text-gray-700">{v.name.trim()}</span></div></td>
                                                <td className="mobile-hide"></td>
                                                <td className="text-right"><span className={`font-medium tabular-nums ${v.quantity <= 0 ? 'text-red-500' : 'text-gray-700'}`}>{v.quantity.toLocaleString('tr-TR')}</span></td>
                                                <td className="mobile-hide text-right">{v.pallets != null ? <span className="text-sm text-gray-500">{v.pallets}</span> : <span className="text-gray-300">-</span>}</td>
                                                {isAdmin && <td className="text-right text-xs text-gray-400">{fmt(vPurchaseVal)}</td>}
                                                {isAdmin && <td className="text-right text-xs text-[#34C759]/70">{fmt(vSaleVal)}</td>}
                                                <td><button onClick={e => { e.stopPropagation(); openAdjust(v, true, p.name); }} className="btn-icon hover:text-blue-600" data-testid={`adjust-variant-${v.id}`}><Edit3 size={14} /></button></td>
                                            </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalPages={Math.ceil(filtered.length / PAGE_SIZE)} onPageChange={setPage} />
                    </>
                ) : <div className="text-center py-16"><Package className="w-12 h-12 text-gray-300 mx-auto mb-4" /><h3 className="text-base font-medium text-gray-900">Stok verisi yok</h3></div>}
            </div>

            {adjustModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setAdjustModal(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden mx-4" onClick={e => e.stopPropagation()} role="dialog" aria-label="Stok Duzenle" data-testid="adjust-stock-modal">
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <h3 className="font-medium text-gray-900">Stok Duzenle</h3>
                            <button onClick={() => setAdjustModal(null)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"><X size={20} className="text-gray-500" /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div><div className="text-sm font-medium text-gray-900">{adjustModal.name}</div><div className="text-xs text-gray-400 mt-0.5">Mevcut stok: {adjustModal.current}</div></div>
                            {adjustModal.hasVariants ? (
                                <><div className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">Varyant stoklarini ayri ayri duzenleyin.</div><button type="button" onClick={() => setAdjustModal(null)} className="btn btn-secondary w-full">Kapat</button></>
                            ) : (
                                <>
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => setAdjustMode('IN')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${adjustMode === 'IN' ? 'bg-green-50 text-green-600 ring-1 ring-green-200' : 'bg-gray-50 text-gray-500'}`} data-testid="adjust-mode-in"><ArrowDownRight size={16} /> Giris</button>
                                        <button type="button" onClick={() => setAdjustMode('OUT')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${adjustMode === 'OUT' ? 'bg-red-50 text-red-500 ring-1 ring-red-200' : 'bg-gray-50 text-gray-500'}`} data-testid="adjust-mode-out"><ArrowUpRight size={16} /> Cikis</button>
                                    </div>
                                    <div><label className="form-label">Miktar</label><input type="number" value={adjustQty} onChange={e => setAdjustQty(e.target.value)} className="form-input text-lg font-semibold text-center" min="1" placeholder="0" autoFocus data-testid="adjust-qty-input" onKeyDown={e => { if (e.key === 'Enter') handleAdjust(); }} /></div>
                                    <div className="flex gap-3">
                                        <button type="button" onClick={() => setAdjustModal(null)} className="btn btn-secondary flex-1" disabled={adjustSaving}>Iptal</button>
                                        <button type="button" onClick={handleAdjust} className={`btn flex-1 ${adjustMode === 'IN' ? 'btn-success' : 'btn-danger'}`} disabled={adjustSaving} data-testid="adjust-save-btn">{adjustSaving ? '...' : 'Kaydet'}</button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ======== MOVEMENTS TAB ========
function MovementsTab() {
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [sortField, setSortField] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');
    const [selected, setSelected] = useState(new Set());
    const [movPage, setMovPage] = useState(1);
    const MOV_PAGE_SIZE = 20;

    useEffect(() => { fetchData(); }, []);
    const fetchData = async () => { try { setMovements((await getMovements()).data); } catch {} finally { setLoading(false); } };

    const handleSort = (field) => { if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(field); setSortDir('asc'); } };

    const flat = useMemo(() => {
        setMovPage(1);
        let list = movements.map(m => ({ ...m, total_qty: m.lines?.reduce((s, l) => s + l.quantity, 0) || 0, product_names: m.lines?.map(l => l.product_name).join(', ') || '', warehouse_names: [...new Set(m.lines?.map(l => l.warehouse_name) || [])].join(', ') }));
        if (typeFilter === 'production') list = list.filter(m => m.is_production);
        else if (typeFilter !== 'all') list = list.filter(m => m.movement_type === typeFilter);
        if (searchQuery) { const q = searchQuery.toLowerCase(); list = list.filter(m => m.product_names.toLowerCase().includes(q) || m.notes?.toLowerCase().includes(q) || m.employee_name?.toLowerCase().includes(q)); }
        list.sort((a, b) => { let va = a[sortField], vb = b[sortField]; if (sortField === 'total_qty') return sortDir === 'asc' ? (va||0)-(vb||0) : (vb||0)-(va||0); return sortDir === 'asc' ? String(va||'').localeCompare(String(vb||''),'tr') : String(vb||'').localeCompare(String(va||''),'tr'); });
        return list;
    }, [movements, searchQuery, typeFilter, sortField, sortDir]);

    const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleAll = () => setSelected(prev => prev.size === flat.length ? new Set() : new Set(flat.map(m => m.id)));
    const handleDelete = async (id) => { if (!window.confirm('Silmek?')) return; try { await deleteMovement(id); toast.success('Silindi'); fetchData(); } catch { toast.error('Silinemedi'); } };
    const printSelected = () => {
        const items = flat.filter(m => selected.has(m.id));
        if (!items.length) return;
        const rows = items.map(m => `<tr><td>${m.movement_type==='IN'?'Giris':'Cikis'}</td><td>${m.product_names}</td><td style="text-align:right;font-weight:bold;color:${m.movement_type==='IN'?'green':'red'}">${m.movement_type==='IN'?'+':'-'}${m.total_qty}</td><td>${m.employee_name || '—'}</td><td>${new Date(m.created_at).toLocaleDateString('tr-TR')}</td></tr>`).join('');
        const w = window.open('','_blank','width=600,height=500');
        w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{font-family:sans-serif;font-size:12px;padding:10mm}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}th{background:#f5f5f5}</style></head><body><h2>Stok Hareketleri</h2><table><thead><tr><th>Tip</th><th>Urunler</th><th>Miktar</th><th>Yapan</th><th>Tarih</th></tr></thead><tbody>${rows}</tbody></table><script>onload=()=>print()</script></body></html>`);
        w.document.close();
    };
    const formatDate = (d) => new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const SortIcon = ({ field }) => { if (sortField !== field) return <ChevronUp size={12} className="opacity-0 group-hover:opacity-30" />; return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />; };
    const ThCell = ({ field, children, align }) => (<th className={`cursor-pointer select-none group ${align === 'right' ? 'text-right' : ''}`} onClick={() => handleSort(field)}><span className="inline-flex items-center gap-1">{children} <SortIcon field={field} /></span></th>);

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Ara..." className="form-input pl-10" data-testid="movement-search" />
                </div>
                <div className="flex gap-1">
                    {[{k:'all',l:'Tumu'},{k:'IN',l:'Giris'},{k:'OUT',l:'Cikis'},{k:'production',l:'Uretim'}].map(b => (
                        <button key={b.k} onClick={() => setTypeFilter(b.k)} className={`btn text-sm ${typeFilter === b.k ? 'btn-primary' : 'btn-secondary'}`} data-testid={`type-filter-${b.k}`}>{b.l}</button>
                    ))}
                </div>
                {selected.size > 0 && <button onClick={printSelected} className="btn btn-secondary text-sm" data-testid="print-btn"><Printer size={14} /> Yazdir ({selected.size})</button>}
            </div>
            <div className="data-card">
                {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-10 rounded" />)}</div>
                : flat.length > 0 ? (
                    <>
                    <div className="table-container">
                        <table className="table mobile-cards" data-testid="movements-table" style={{ tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '40px' }} />
                                <col style={{ width: '70px' }} />
                                <col />
                                <col style={{ width: '80px' }} />
                                <col style={{ width: '100px' }} />
                                <col style={{ width: '120px' }} />
                                <col style={{ width: '120px' }} />
                                <col style={{ width: '40px' }} />
                            </colgroup>
                            <thead><tr>
                                <th className="w-10"><input type="checkbox" checked={selected.size===flat.length&&flat.length>0} onChange={toggleAll} className="rounded border-gray-300" /></th>
                                <ThCell field="movement_type">Tip</ThCell>
                                <ThCell field="product_names">Urunler</ThCell>
                                <ThCell field="total_qty" align="right">Miktar</ThCell>
                                <ThCell field="employee_name">Yapan</ThCell>
                                <ThCell field="notes">Not</ThCell>
                                <ThCell field="created_at">Tarih</ThCell>
                                <th className="w-10"></th>
                            </tr></thead>
                            <tbody>{flat.slice((movPage - 1) * MOV_PAGE_SIZE, movPage * MOV_PAGE_SIZE).map(m => (
                                <tr key={m.id} data-testid={`movement-row-${m.id}`} className={selected.has(m.id) ? 'bg-blue-50' : ''}>
                                    <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleSelect(m.id)} className="rounded border-gray-300" /></td>
                                    <td><span className={`badge ${m.movement_type==='IN'?'badge-success':'badge-danger'}`}>{m.movement_type==='IN'?'Giris':'Cikis'}</span>{m.is_production && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">Uretim</span>}</td>
                                    <td className="font-medium text-gray-900 text-sm truncate">{m.product_names}</td>
                                    <td className={`text-right font-semibold ${m.movement_type==='IN'?'text-green-600':'text-red-500'}`}>{m.movement_type==='IN'?'+':'-'}{m.total_qty}</td>
                                    <td className="text-gray-500 text-xs truncate">{m.employee_name || '—'}</td>
                                    <td className="text-gray-400 text-xs truncate" title={m.notes || ''}>{m.notes || '—'}</td>
                                    <td className="text-gray-400 text-xs">{formatDate(m.created_at)}</td>
                                    <td><button onClick={() => handleDelete(m.id)} className="btn-icon hover:text-red-500" data-testid={`del-${m.id}`}><Trash2 size={14} /></button></td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                    <Pagination currentPage={movPage} totalPages={Math.ceil(flat.length / MOV_PAGE_SIZE)} onPageChange={setMovPage} />
                    </>
                ) : <div className="text-center py-16"><ArrowLeftRight className="w-12 h-12 text-gray-300 mx-auto mb-4" /><h3 className="text-base font-medium text-gray-900">Henuz hareket yok</h3></div>}
            </div>
        </div>
    );
}

// ======== WAREHOUSES TAB ========
function WarehousesTab() {
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', address: '', is_default: false });

    useEffect(() => { loadData(); }, []);
    const loadData = async () => { try { setWarehouses((await getWarehouses()).data); } catch {} finally { setLoading(false); } };

    const openModal = (wh = null) => {
        if (wh) { setEditing(wh); setForm({ name: wh.name, address: wh.address || '', is_default: wh.is_default || false }); }
        else { setEditing(null); setForm({ name: '', address: '', is_default: false }); }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error('Isim gerekli');
        try {
            if (editing) { await updateWarehouse(editing.id, form); toast.success('Guncellendi'); }
            else { await createWarehouse(form); toast.success('Eklendi'); }
            setShowModal(false); loadData();
        } catch { toast.error('Hata'); }
    };

    const handleDelete = async (id) => { if (!window.confirm('Silmek?')) return; try { await deleteWarehouse(id); toast.success('Silindi'); loadData(); } catch { toast.error('Silinemedi'); } };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">{warehouses.length} depo</p>
                <button onClick={() => openModal()} className="btn btn-primary text-sm" data-testid="add-warehouse-btn"><Plus size={14} /> Depo Ekle</button>
            </div>
            {loading ? <div className="space-y-2">{[1,2].map(i => <div key={i} className="skeleton h-12 rounded-xl" />)}</div>
            : warehouses.length > 0 ? (
                <div className="space-y-0.5" data-testid="warehouses-list">
                    {warehouses.map(wh => (
                        <div key={wh.id} className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-gray-50 transition-colors group" data-testid={`warehouse-${wh.id}`}>
                            <div className="flex items-center gap-3">
                                <WarehouseIcon size={16} className="text-gray-400" />
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{wh.name}</span>
                                        {wh.is_default && <Star size={12} className="text-amber-500 fill-amber-500" />}
                                    </div>
                                    {wh.address && <div className="text-xs text-gray-400">{wh.address}</div>}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => openModal(wh)} className="btn-icon hover:text-blue-600"><Edit2 size={14} /></button>
                                <button onClick={() => handleDelete(wh.id)} className="btn-icon hover:text-red-500"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-16"><WarehouseIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" /><h3 className="text-base font-medium text-gray-900 mb-3">Henuz depo yok</h3><button onClick={() => openModal()} className="btn btn-primary text-sm"><Plus size={14} /> Depo Ekle</button></div>
            )}

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden mx-4" onClick={e => e.stopPropagation()} role="dialog" aria-label="Depo formu" data-testid="warehouse-modal">
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <h3 className="font-medium text-gray-900">{editing ? 'Depo Duzenle' : 'Depo Ekle'}</h3>
                            <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"><X size={20} className="text-gray-500" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-4 space-y-3">
                            <div><label className="form-label">Isim *</label><input type="text" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="form-input text-sm" required autoFocus data-testid="warehouse-name-input" /></div>
                            <div><label className="form-label">Adres</label><input type="text" value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} className="form-input text-sm" /></div>
                            <div className="flex items-center gap-2"><input type="checkbox" checked={form.is_default} onChange={e => setForm(p => ({...p, is_default: e.target.checked}))} className="rounded border-gray-300" /><label className="text-sm text-gray-700">Varsayilan depo</label></div>
                            <div className="flex gap-3"><button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary flex-1">Iptal</button><button type="submit" className="btn btn-primary flex-1" data-testid="save-warehouse-btn">Kaydet</button></div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

function printTerminalQR(url, companyName) {
    const w = window.open('', '_blank', 'width=400,height=500');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{font-family:sans-serif;text-align:center;padding:40px}h2{margin:0 0 8px;font-size:20px}p{color:#666;font-size:13px;margin:4px 0 30px}svg{width:280px;height:280px}.url{font-size:11px;color:#999;margin-top:20px;word-break:break-all}</style></head><body><h2>${companyName || 'Flowbil'}</h2><p>Stok Terminal</p><div id="qr"></div><p class="url">${url}</p><script src="https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js"><\/script><script>var q=qrcode(0,'M');q.addData('${url}');q.make();document.getElementById('qr').innerHTML=q.createSvgTag({cellSize:8,margin:0});setTimeout(()=>print(),500)<\/script></body></html>`);
}
